/**
 * Auth callback (email confirmation / OAuth-style redirects).
 *
 * Handles the provider response safely:
 * - explicit error params in query or hash are surfaced in plain Dutch;
 * - a valid exchange produces a session, the auth provider picks it up, and
 *   we route to the correct portal;
 * - if no session materializes within a grace period we say so honestly and
 *   send the user back to login. No silent entry, no fake success.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, StatusCard } from '@/components/ui'
import { PATHS } from '@/app/router/paths'
import { SessionLoadingScreen, portalHomeForRole } from '@/app/router/RequireAuth'
import { isCloudConfigured } from '@/lib/supabase/client'
import { useAuth } from '../useAuth'
import { AuthLayout, AuthLegalFooter, NotConfiguredBanner, RustWordmark } from './AuthLayout'

const GRACE_PERIOD_MS = 6000

function readProviderError(): string | null {
  const search = new URLSearchParams(window.location.search)
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const code = search.get('error') ?? hash.get('error')
  const description = search.get('error_description') ?? hash.get('error_description')
  if (!code && !description) return null
  if (code === 'access_denied') {
    return 'De link is geannuleerd of verlopen. Vraag eventueel een nieuwe aan.'
  }
  // Provider descriptions are technical English; keep them out of the UI.
  return 'De bevestigingslink kon niet worden verwerkt. Probeer het opnieuw.'
}

export default function AuthCallbackPage() {
  const { status, role } = useAuth()
  const navigate = useNavigate()
  const configured = isCloudConfigured()
  const providerError = useMemo(readProviderError, [])
  const [graceExpired, setGraceExpired] = useState(false)

  useEffect(() => {
    if (status !== 'unauthenticated') return
    const timer = window.setTimeout(() => setGraceExpired(true), GRACE_PERIOD_MS)
    return () => window.clearTimeout(timer)
  }, [status])

  useEffect(() => {
    if (status === 'authenticated' && role) {
      navigate(portalHomeForRole(role), { replace: true })
    }
  }, [status, role, navigate])

  if (!configured) {
    return (
      <AuthLayout backTo={PATHS.welcome} backLabel="Terug naar start">
        <RustWordmark width={140} />
        <div className="auth-card">
          <NotConfiguredBanner />
        </div>
        <AuthLegalFooter />
      </AuthLayout>
    )
  }

  if (providerError || (status === 'unauthenticated' && graceExpired)) {
    return (
      <AuthLayout>
        <RustWordmark width={140} />
        <h1 className="auth-headline" style={{ fontSize: 26 }}>
          Bevestigen mislukt
        </h1>
        <div className="auth-card">
          <StatusCard
            status="warning"
            title="We konden je niet automatisch inloggen"
            body={providerError ?? 'De link is mogelijk verlopen. Log opnieuw in of vraag een nieuwe bevestigingsmail aan.'}
            action={
              <Button variant="primary" fullWidth onClick={() => navigate(PATHS.role, { replace: true })}>
                Naar inloggen
              </Button>
            }
          />
        </div>
        <AuthLegalFooter />
      </AuthLayout>
    )
  }

  return <SessionLoadingScreen label="Je wordt ingelogd…" />
}
